import { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Card,
  Modal,
  Form,
  Pagination,
} from "react-bootstrap";
import axios from "axios";
import * as XLSX from "xlsx"; // Import xlsx library
import { saveAs } from "file-saver"; // Import file-saver

const ManageTag = () => {
  const [records, setRecords] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tenantName, setTenantName] = useState("");
  const [officeList, setOfficeList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    vehicle_number: "",
    vehicle_type: "",
    block_status: "",
    userName: "",
    card_no: "",
  });

  const itemsPerPage = 10;

  // Fetch records on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://housys.skymarkone.in:8001/api/get-tags/${tenantName}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const data = response.data;

        if (data.status === "success") {
          setRecords(data.data); // Assuming data.data contains the tags
        } else {
          console.error("Data is not in the expected format:", data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [tenantName]);

  // Second useEffect for fetching office data
  useEffect(() => {
    const fetchAllOffices = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("No token found, please log in.");
          return;
        }

        const response = await axios.get("https://housys.skymarkone.in:8001/api/getoffice", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data && response.data.office) {
          const officeData = Array.isArray(response.data.office)
            ? response.data.office
            : [response.data.office];
          setOfficeList(officeData);
          console.log(officeData);
        } else {
          console.error("Error fetching offices:", response.data.msg);
        }
      } catch (error) {
        console.error(
          "Error fetching all offices:",
          error.response?.data || error.message
        );
      }
    };

    fetchAllOffices(); // Fetch office data on component mount
  }, []);

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      vehicle_number: record.vehicle_number,
      vehicle_type: record.vehicle_type,
      block_status: record.block_status,
      userName: record.userName,
      card_no: record.card_no,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
  };

  const handleSaveChanges = async () => {
    const updatedData = {
      ...formData,
      office_id: localStorage.getItem("office_id"),
    };

    try {
      const response = await axios.post(
        `https://housys.skymarkone.in:8001/api/edit-tag/${selectedRecord.id}`,
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.status === "success") {
        setRecords((prevRecords) =>
          prevRecords.map((record) =>
            record.id === selectedRecord.id ? { ...record, ...updatedData } : record
          )
        );
        setShowModal(false); // Close modal after success
      } else {
        console.error("Error in response:", response.data);
      }
    } catch (error) {
      console.error("Error updating record:", error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.post(
        `https://housys.skymarkone.in:8001/api/delete-tag/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.data.status === "success") {
        setRecords(records.filter((record) => record.id !== id));
      }
    } catch (error) {
      console.error("Error deleting record:", error);
    }
  };

  // Handle Excel download - now uses filtered records
  const handleDownloadExcel = () => {
    const dataToExport = filteredRecords; // Use filtered records instead of all records
    
    if (dataToExport.length === 0) {
      return; // Do nothing if no records
    }

    const formatDateTime = (datetime) => {
      if (!datetime) return "N/A";
      const date = new Date(datetime);
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      let hours = date.getUTCHours();
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const hoursStr = String(hours).padStart(2, '0');
      return `${day}/${month}/${year}, ${hoursStr}:${minutes}:${seconds} ${ampm}`;
    };

    const columns = ["#", "User", "Vehicle No", "Type", "Status", "Card No", "Date Time"];
    const rows = dataToExport.map((record, index) => [
      index + 1,
      record.User.name,
      record.vehicle_number,
      record.vehicle_type,
      record.block_status,
      record.card_no,
      formatDateTime(record.createdAt),
    ]);

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
    
    // Define styles for headers
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "28A745" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };
    
    // Define styles for data cells
    const dataStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } }
      }
    };

    // Apply styles to headers (first row)
    columns.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (!ws[cellAddress]) ws[cellAddress] = {};
      ws[cellAddress].s = headerStyle;
    });

    // Apply styles to data cells
    rows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
        if (!ws[cellAddress]) ws[cellAddress] = {};
        ws[cellAddress].s = dataStyle;
      });
    });

    // Set column widths
    ws['!cols'] = [
      { wch: 8 },  // #
      { wch: 20 }, // User
      { wch: 15 }, // Vehicle No
      { wch: 10 }, // Type
      { wch: 12 }, // Status
      { wch: 25 }, // Card No
      { wch: 25 }  // Date Time
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Manage Tags");

    // Get tenant name for filename
    const selectedTenant = officeList.find(office => office.office_id === tenantName);
    const tenantDisplayName = selectedTenant ? selectedTenant.office_name.replace(/[^a-zA-Z0-9]/g, '_') : "All_Tenants";
    
    // Format current date and time for filename
    const now = new Date();
    const dateTime = now.toISOString().slice(0, 19).replace(/[:]/g, '-').replace('T', '_');
    
    // Create informative filename with search indicator
    const searchIndicator = searchTerm ? `_Filtered_${searchTerm.replace(/[^a-zA-Z0-9]/g, '_')}` : "";
    const fileName = `ManageTags_${tenantDisplayName}${searchIndicator}_${dateTime}.xlsx`;

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const file = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(file, fileName);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Filter records based on search term
  const filteredRecords = records.filter((record) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      record.User.name.toLowerCase().includes(searchLower) ||
      record.vehicle_number.toLowerCase().includes(searchLower) ||
      record.vehicle_type.toLowerCase().includes(searchLower) ||
      record.card_no.toLowerCase().includes(searchLower) ||
      record.block_status.toString().toLowerCase().includes(searchLower)
    );
  });

  const indexOfLastRecord = currentPage * itemsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - itemsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);

  return (
    <Container fluid className="p-4" style={{ 
      minHeight: "100vh", 
      background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)" 
    }}>
      {/* Header Section */}
      <Row className="mb-4">
        <Col xs={12} lg={8} className="mb-3 mb-lg-0">
          <div className="d-flex align-items-center mb-3">
            <div 
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "15px",
                background: "linear-gradient(135deg, #664CBE 0%, #4472C4 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "15px"
              }}
            >
              <i className="bi bi-tags text-white" style={{ fontSize: "24px" }}></i>
            </div>
            <div>
              <h2 style={{ 
                color: "#212529", 
                fontWeight: "700",
                fontSize: "1.75rem",
                marginBottom: "5px"
              }}>
                Manage Registered Users and Assign Cards
              </h2>
              <p style={{ 
                color: "#6c757d", 
                marginBottom: "0",
                fontSize: "1rem"
              }}>
                All registered users with complete details and tag management
              </p>
            </div>
          </div>
        </Col>
        <Col
          xs={12}
          lg={4}
          className="d-flex justify-content-lg-end align-items-center"
        >
          <Button
            style={{
              background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
              border: "none",
              borderRadius: "12px",
              padding: "12px 24px",
              fontWeight: "600",
              boxShadow: "0 4px 15px rgba(40, 167, 69, 0.3)",
              transition: "all 0.3s ease"
            }}
            onClick={handleDownloadExcel}
            disabled={filteredRecords.length === 0}
            onMouseEnter={(e) => {
              if (!e.target.disabled) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 20px rgba(40, 167, 69, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 15px rgba(40, 167, 69, 0.3)";
            }}
          >
            <i className="bi bi-download me-2"></i>
            Download Excel {searchTerm && `(${filteredRecords.length} filtered)`}
          </Button>
        </Col>
      </Row>

      {/* Controls Section */}
      <Row className="mb-4">
        <Col xs={12} md={6} className="mb-3 mb-md-0">
          <Form.Group controlId="formTenant">
            <Form.Label style={{
              fontWeight: "600",
              color: "#495057",
              marginBottom: "8px"
            }}>
              <i className="bi bi-building me-2 text-primary"></i>
              Select Tenant
            </Form.Label>
            <Form.Control
              as="select"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              style={{
                borderRadius: "10px",
                border: "2px solid #e9ecef",
                padding: "12px 16px",
                fontSize: "1rem",
                transition: "all 0.3s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#664CBE";
                e.target.style.boxShadow = "0 0 0 0.2rem rgba(102, 76, 190, 0.25)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e9ecef";
                e.target.style.boxShadow = "none";
              }}
            >
              <option value="">Choose tenant...</option>
              {officeList.map((office, index) => (
                <option key={index} value={office.office_id}>
                  {office.office_name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        
        <Col xs={12} md={6}>
          <Form.Group controlId="formSearch">
            <Form.Label style={{
              fontWeight: "600",
              color: "#495057",
              marginBottom: "8px"
            }}>
              <i className="bi bi-search me-2 text-info"></i>
              Search Records
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by user name, vehicle number, card number..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                borderRadius: "10px",
                border: "2px solid #e9ecef",
                padding: "12px 16px",
                fontSize: "1rem",
                transition: "all 0.3s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#17a2b8";
                e.target.style.boxShadow = "0 0 0 0.2rem rgba(23, 162, 184, 0.25)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e9ecef";
                e.target.style.boxShadow = "none";
              }}
            />
            {searchTerm && (
              <small className="text-muted">
                <i className="bi bi-info-circle me-1"></i>
                Showing {filteredRecords.length} of {records.length} records
              </small>
            )}
          </Form.Group>
        </Col>
      </Row>

      {/* Table Section */}
      <Row>
        <Col xs={12}>
          <Card 
            className="mt-3" 
            style={{
              border: "none",
              borderRadius: "20px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              background: "#ffffff"
            }}
          >
            <Card.Header style={{
              background: "#ffffff",
              borderBottom: "2px solid #dee2e6",
              borderRadius: "20px 20px 0 0",
              padding: "20px 25px"
            }}>
              <div className="d-flex align-items-center">
                <i className="bi bi-table me-3 text-primary" style={{ fontSize: "20px" }}></i>
                <strong style={{ 
                  fontSize: "1.1rem", 
                  color: "#495057",
                  fontWeight: "700"
                }}>
                  Manage Registered User and Tag
                </strong>
                {searchTerm && (
                  <span style={{
                    marginLeft: "15px",
                    padding: "4px 12px",
                    backgroundColor: "#e3f2fd",
                    color: "#1565c0",
                    borderRadius: "20px",
                    fontSize: "0.85rem",
                    fontWeight: "600"
                  }}>
                    <i className="bi bi-funnel me-1"></i>
                    Filtered Results
                  </span>
                )}
              </div>
            </Card.Header>
            <Card.Body style={{ padding: "0" }}>
              <div style={{ overflowX: "auto" }}>
                <Table 
                  hover 
                  responsive 
                  style={{ 
                    marginBottom: "0",
                    borderCollapse: "separate",
                    borderSpacing: "0"
                  }}
                >
                  <thead>
                    <tr style={{ 
                      background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                    }}>
                      <th style={{ 
                        border: "none", 
                        padding: "18px 20px",
                        fontWeight: "700",
                        color: "#495057",
                        fontSize: "0.9rem"
                      }}>
                        <i className="bi bi-hash me-2 text-secondary"></i>#
                      </th>
                      <th style={{ 
                        border: "none", 
                        padding: "18px 20px",
                        fontWeight: "700",
                        color: "#495057",
                        fontSize: "0.9rem"
                      }}>
                        <i className="bi bi-person me-2 text-primary"></i>Registered User
                      </th>
                      <th style={{ 
                        border: "none", 
                        padding: "18px 20px",
                        fontWeight: "700",
                        color: "#495057",
                        fontSize: "0.9rem"
                      }}>
                        <i className="bi bi-car-front me-2 text-success"></i>Vehicle Number
                      </th>
                      <th style={{ 
                        border: "none", 
                        padding: "18px 20px",
                        fontWeight: "700",
                        color: "#495057",
                        fontSize: "0.9rem"
                      }}>
                        <i className="bi bi-truck me-2 text-info"></i>Vehicle Type
                      </th>
                      <th style={{ 
                        border: "none", 
                        padding: "18px 20px",
                        fontWeight: "700",
                        color: "#495057",
                        fontSize: "0.9rem"
                      }}>
                        <i className="bi bi-circle-fill me-2 text-warning"></i>Status
                      </th>
                      <th style={{ 
                        border: "none", 
                        padding: "18px 20px",
                        fontWeight: "700",
                        color: "#495057",
                        fontSize: "0.9rem"
                      }}>
                        <i className="bi bi-credit-card me-2 text-purple"></i>Card Number
                      </th>
                      <th style={{ 
                        border: "none", 
                        padding: "18px 20px",
                        fontWeight: "700",
                        color: "#495057",
                        fontSize: "0.9rem"
                      }}>
                        <i className="bi bi-calendar me-2 text-secondary"></i>Date Time
                      </th>
                      <th style={{ 
                        border: "none", 
                        padding: "18px 20px",
                        fontWeight: "700",
                        color: "#495057",
                        fontSize: "0.9rem"
                      }}>
                        <i className="bi bi-gear me-2 text-dark"></i>Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRecords.length > 0 ? (
                      currentRecords.map((record, index) => (
                        <tr 
                          key={record.id}
                          style={{
                            transition: "all 0.3s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          <td style={{ 
                            padding: "16px 20px",
                            borderBottom: "1px solid #e9ecef",
                            color: "#6c757d",
                            fontWeight: "600"
                          }}>
                            {index + 1 + indexOfFirstRecord}
                          </td>
                          <td style={{ 
                            padding: "16px 20px",
                            borderBottom: "1px solid #e9ecef",
                            color: "#212529",
                            fontWeight: "600"
                          }}>
                            <div className="d-flex align-items-center">
                              <div 
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "8px",
                                  background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginRight: "12px"
                                }}
                              >
                                <i className="bi bi-person text-white" style={{ fontSize: "14px" }}></i>
                              </div>
                              {record.User.name}
                            </div>
                          </td>
                          <td style={{ 
                            padding: "16px 20px",
                            borderBottom: "1px solid #e9ecef",
                            color: "#212529"
                          }}>
                            <span style={{
                              padding: "6px 12px",
                              backgroundColor: "#e8f5e8",
                              color: "#155724",
                              borderRadius: "8px",
                              fontSize: "0.9rem",
                              fontWeight: "600"
                            }}>
                              {record.vehicle_number}
                            </span>
                          </td>
                          <td style={{ 
                            padding: "16px 20px",
                            borderBottom: "1px solid #e9ecef",
                            color: "#212529"
                          }}>
                            <span style={{
                              padding: "6px 12px",
                              backgroundColor: record.vehicle_type === "Car" ? "#e3f2fd" : "#fff3cd",
                              color: record.vehicle_type === "Car" ? "#1565c0" : "#856404",
                              borderRadius: "20px",
                              fontSize: "0.85rem",
                              fontWeight: "600"
                            }}>
                              <i className={`bi ${record.vehicle_type === "4W" ? "bi-car-front" : "bi-bicycle"} me-1`}></i>
                              {record.vehicle_type}
                            </span>
                          </td>
                          <td style={{ 
                            padding: "16px 20px",
                            borderBottom: "1px solid #e9ecef",
                            color: "#212529"
                          }}>
                            <span style={{
                              padding: "6px 12px",
                              backgroundColor: record.block_status === "Active" ? "#d4edda" : "#f8d7da",
                              color: record.block_status === "Active" ? "#155724" : "#721c24",
                              borderRadius: "20px",
                              fontSize: "0.85rem",
                              fontWeight: "600"
                            }}>
                              <i className={`bi ${record.block_status === "Active" ? "bi-check-circle" : "bi-x-circle"} me-1`}></i>
                              {record.block_status}
                            </span>
                          </td>
                          <td style={{ 
                            padding: "16px 20px",
                            borderBottom: "1px solid #e9ecef",
                            color: "#212529"
                          }}>
                            <span style={{
                              padding: "6px 12px",
                              backgroundColor: "#f8f0ff",
                              color: "#664CBE",
                              borderRadius: "8px",
                              fontSize: "0.9rem",
                              fontWeight: "600",
                              fontFamily: "monospace"
                            }}>
                              {record.card_no}
                            </span>
                          </td>
                          <td style={{ 
                            padding: "16px 20px",
                            borderBottom: "1px solid #e9ecef",
                            color: "#6c757d",
                            fontSize: "0.9rem"
                          }}>
                            {new Date(record.createdAt).toLocaleString('en-GB', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: true
                            })}
                          </td>
                          <td style={{ 
                            padding: "16px 20px",
                            borderBottom: "1px solid #e9ecef"
                          }}>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleEdit(record)}
                                style={{
                                  background: "linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)",
                                  border: "none",
                                  borderRadius: "8px",
                                  padding: "8px 12px",
                                  transition: "all 0.3s ease"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = "translateY(-2px)";
                                  e.target.style.boxShadow = "0 4px 12px rgba(255, 193, 7, 0.4)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "translateY(0)";
                                  e.target.style.boxShadow = "none";
                                }}
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleDelete(record.id)}
                                style={{
                                  background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                                  border: "none",
                                  borderRadius: "8px",
                                  padding: "8px 12px",
                                  transition: "all 0.3s ease"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = "translateY(-2px)";
                                  e.target.style.boxShadow = "0 4px 12px rgba(220, 53, 69, 0.4)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "translateY(0)";
                                  e.target.style.boxShadow = "none";
                                }}
                              >
                                <i className="bi bi-trash-fill"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td 
                          colSpan="8" 
                          style={{ 
                            textAlign: "center",
                            padding: "40px 20px",
                            color: "#6c757d",
                            border: "none"
                          }}
                        >
                          <div>
                            <i className="bi bi-inbox" style={{ fontSize: "3rem", color: "#dee2e6", marginBottom: "15px" }}></i>
                            <h5 style={{ color: "#6c757d", fontWeight: "600" }}>No Records Found</h5>
                            <p className="mb-0" style={{ fontSize: "0.9rem" }}>
                              {searchTerm ? `No records match "${searchTerm}"` : "No records available for the selected tenant."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              
              {/* Pagination */}
              {filteredRecords.length > itemsPerPage && (
                <div style={{ padding: "20px 25px", borderTop: "1px solid #e9ecef" }}>
                  <Pagination className="justify-content-center gap-1">
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    
                    />
                    {Array.from({ length: Math.min(8, totalPages) }, (_, index) => {
                      const page = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + index;
                      if (page > totalPages) return null;
                      return (
                        <Pagination.Item
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          active={currentPage === page}
                          
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal}
        centered
        size="lg"
      >
        <Modal.Header 
          closeButton
          style={{
            background: "linear-gradient(135deg, #664CBE 0%, #4472C4 100%)",
            color: "white",
            border: "none",
            borderRadius: "15px 15px 0 0"
          }}
        >
          <Modal.Title style={{ 
            fontWeight: "700",
            fontSize: "1.25rem",
            display: "flex",
            alignItems: "center"
          }}>
            <i className="bi bi-pencil-square me-3"></i>
            Edit Registered User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ 
          padding: "30px",
          background: "#f8f9fa"
        }}>
          <Form>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group controlId="formVehicleNumber">
                  <Form.Label style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "8px"
                  }}>
                    <i className="bi bi-car-front me-2 text-success"></i>
                    Vehicle Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="vehicle_number"
                    value={formData.vehicle_number}
                    onChange={handleChange}
                    style={{
                      borderRadius: "10px",
                      border: "2px solid #e9ecef",
                      padding: "12px 16px",
                      fontSize: "1rem",
                      transition: "all 0.3s ease"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#28a745";
                      e.target.style.boxShadow = "0 0 0 0.2rem rgba(40, 167, 69, 0.25)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e9ecef";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group controlId="formVehicleType">
                  <Form.Label style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "8px"
                  }}>
                    <i className="bi bi-truck me-2 text-info"></i>
                    Vehicle Type
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="vehicle_type"
                    value={formData.vehicle_type}
                    onChange={handleChange}
                    style={{
                      borderRadius: "10px",
                      border: "2px solid #e9ecef",
                      padding: "12px 16px",
                      fontSize: "1rem",
                      transition: "all 0.3s ease"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#17a2b8";
                      e.target.style.boxShadow = "0 0 0 0.2rem rgba(23, 162, 184, 0.25)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e9ecef";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group controlId="formBlockStatus">
                  <Form.Label style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "8px"
                  }}>
                    <i className="bi bi-circle-fill me-2 text-warning"></i>
                    Status
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="block_status"
                    value={formData.block_status}
                    onChange={handleChange}
                    style={{
                      borderRadius: "10px",
                      border: "2px solid #e9ecef",
                      padding: "12px 16px",
                      fontSize: "1rem",
                      transition: "all 0.3s ease"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#ffc107";
                      e.target.style.boxShadow = "0 0 0 0.2rem rgba(255, 193, 7, 0.25)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e9ecef";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group controlId="formUserName">
                  <Form.Label style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "8px"
                  }}>
                    <i className="bi bi-person me-2 text-primary"></i>
                    Username
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="userName"
                    value={formData.userName}
                    onChange={handleChange}
                    style={{
                      borderRadius: "10px",
                      border: "2px solid #e9ecef",
                      padding: "12px 16px",
                      fontSize: "1rem",
                      transition: "all 0.3s ease"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#007bff";
                      e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 123, 255, 0.25)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e9ecef";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group controlId="formCardNo" className="mb-3">
              <Form.Label style={{
                fontWeight: "600",
                color: "#495057",
                marginBottom: "8px"
              }}>
                <i className="bi bi-credit-card me-2" style={{ color: "#664CBE" }}></i>
                Card Number
              </Form.Label>
              <Form.Control
                type="text"
                name="card_no"
                value={formData.card_no}
                onChange={handleChange}
                style={{
                  borderRadius: "10px",
                  border: "2px solid #e9ecef",
                  padding: "12px 16px",
                  fontSize: "1rem",
                  transition: "all 0.3s ease",
                  fontFamily: "monospace"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#664CBE";
                  e.target.style.boxShadow = "0 0 0 0.2rem rgba(102, 76, 190, 0.25)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e9ecef";
                  e.target.style.boxShadow = "none";
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ 
          padding: "20px 30px",
          borderTop: "1px solid #e9ecef",
          background: "#ffffff",
          borderRadius: "0 0 15px 15px"
        }}>
          <Button 
            variant="secondary" 
            onClick={handleCloseModal}
            style={{
              borderRadius: "10px",
              padding: "10px 20px",
              fontWeight: "600",
              border: "2px solid #6c757d",
              color: "#6c757d",
              background: "transparent",
              transition: "all 0.3s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#6c757d";
              e.target.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "#6c757d";
            }}
          >
            <i className="bi bi-x-lg me-2"></i>
            Close
          </Button>
          <Button 
            onClick={handleSaveChanges}
            style={{
              background: "linear-gradient(135deg, #664CBE 0%, #4472C4 100%)",
              border: "none",
              borderRadius: "10px",
              padding: "10px 20px",
              fontWeight: "600",
              transition: "all 0.3s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 4px 15px rgba(102, 76, 190, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "none";
            }}
          >
            <i className="bi bi-check-lg me-2"></i>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ManageTag;
